// Lens registry + resolution cascade — the spine of the explore surface.
//
// Every center-panel view is a "lens": a (surface descriptor, body) pair bound
// to a point in the scope/dimension navigation. Which lens renders is resolved
// from the current nav target by specificity, always falling through to the
// scope overview so the center panel is never blank.
//
// Three levels, most-specific wins:
//
//   value      — a specific dimension VALUE is selected (the terminal scope
//                step). e.g. one skill -> a content-compare lens. If no value
//                lens matches but the dim itself has a dimension lens (e.g.
//                SIGNALS), that resolves instead — so a signal acts as a normal
//                selectable entry (highlights + expands) yet still opens its
//                lens; otherwise falls through to the scope overview of
//                (parent + value). A value lens may bind to all values of a dim,
//                or to a subset via a `when(step)` predicate (predicate wins).
//   dimension  — a dimension is FOCUSED with no value committed yet (focusDim
//                set). e.g. SIGNALS -> an alert console aggregating across all
//                signals. Falls through to the generic member-rollup
//                (the dimension lens registered with no `dim`), then to scope.
//   scope      — universal lenses available at EVERY scope. The first one
//                registered (the overview — the event/trace stream for
//                whatever scope is committed) is the fallback/default;
//                later registrations (e.g. the endpoint Inventory) ride
//                along as always-available toggles after it.
//
// The resolver is pure and instance-scoped: createLensRegistry() returns an
// isolated registry, so tests build their own without touching app state.
// See lensRegistry.test.js.
//
// Nav target (LensTarget):
//   scopePath: ScopeStep[]   committed scope; ScopeStep = { dim, ids, id }
//                            (see decodeScopePath in dimensionDefs.js).
//   focusDim:  string|null   a dimension focused without a committed value ->
//                            dimension level. This is a SEPARATE nav field, not
//                            a scope step: decodeScopePath reserves an empty
//                            step for the "(no value)" bucket, so focus can't
//                            ride an empty step. How the URL carries focusDim is
//                            wired alongside the SIGNALS dimension.

/** @typedef {{ dim: string, ids: string[], id: string }} ScopeStep */
/** @typedef {{ scopePath?: ScopeStep[], focusDim?: string|null }} LensTarget */
/** @typedef {{ value: string, label: string, kind?: string }} SurfaceTab */
/** @typedef {{ tabs: SurfaceTab[], defaultTab?: string }} SurfaceDescriptor */
/**
 * @typedef {Object} Lens
 * @property {string} id                       unique, e.g. "scope.overview", "dimension.signals"
 * @property {"scope"|"dimension"|"value"} level
 * @property {string} [dim]                    plural dimension key for dimension/value lenses
 * @property {(step: ScopeStep) => boolean} [when]   value lenses: bind to a subset of values
 * @property {SurfaceDescriptor|null} [surface]      per-lens activity surface; null = no surface band
 * @property {Function} Body                   React component; receives the resolved nav context
 * @property {string} [title]
 * @property {boolean} [defaultActive]         within a resolved set, this lens is the default
 *                                             active one when no `view` is chosen (else the
 *                                             scope overview, else the first)
 * @property {boolean} [dimensionOnly]         dimension lenses only: bind to the FOCUSED-dim
 *                                             level exclusively, never as a fallback for a
 *                                             committed value of the same dim. Signals wants a
 *                                             selected value to open its console (default: it
 *                                             does); an aggregate like the endpoint inventory
 *                                             does not — a single selected endpoint should show
 *                                             its overview, not a one-row inventory.
 */

/**
 * The default active lens within a resolved set: an explicit `defaultActive`
 * lens, else the scope overview, else the first. LensHost and the sidebar lens
 * links use this to agree on what's shown when no `view` is selected.
 * @param {Lens[]} lenses
 * @returns {Lens|null}
 */
export function defaultLensOf(lenses) {
  if (!lenses || !lenses.length) return null;
  return lenses.find((l) => l.defaultActive)
    || lenses.find((l) => l.level === "scope")
    || lenses[0];
}

const LEVELS = new Set(["scope", "dimension", "value"]);

function validateLens(lens) {
  if (!lens || typeof lens !== "object") throw new Error("registerLens: lens must be an object");
  if (typeof lens.id !== "string" || !lens.id) throw new Error("registerLens: lens.id required");
  if (!LEVELS.has(lens.level)) throw new Error(`registerLens: invalid level "${lens.level}" (${lens.id})`);
  if (lens.level === "value" && !lens.dim) throw new Error(`registerLens: value lens "${lens.id}" requires dim`);
  if (typeof lens.Body !== "function") throw new Error(`registerLens: lens "${lens.id}" requires a Body component`);
}

export function createLensRegistry() {
  const store = { scopeLenses: [], dimensionDefault: null, dimension: Object.create(null), value: [] };

  /**
   * Register a lens. Scope lenses accumulate (same-id re-registration
   * replaces in place — the old last-write-wins, per lens); the FIRST
   * registered scope lens is the universal fallback/default. Last write
   * wins for the generic dimension lens and per-dim dimension lenses;
   * value lenses accumulate (resolution order = registration order among
   * equally-specific matches).
   * @param {Lens} lens
   * @returns the registry (chainable)
   */
  function register(lens) {
    validateLens(lens);
    if (lens.level === "scope") {
      const i = store.scopeLenses.findIndex((l) => l.id === lens.id);
      if (i >= 0) store.scopeLenses[i] = lens;
      else store.scopeLenses.push(lens);
    } else if (lens.level === "dimension") {
      if (lens.dim) store.dimension[lens.dim] = lens;
      else store.dimensionDefault = lens;
    } else store.value.push(lens);
    return api;
  }

  /**
   * Resolve the ORDERED set of lenses applicable to a nav target — most-
   * specific first, always ending with the scope overview so it stays
   * available as a toggle (the "two options" model: a value/dimension exposes
   * its custom lens AND the generic scope overview, switchable). The first
   * entry is the default. LensHost renders a switcher when there's >1.
   * @param {LensTarget} target
   * @returns {Lens[]} possibly empty (only when no scope lens is registered).
   */
  function resolveLenses(target) {
    const focusDim = target?.focusDim || null;
    const scopePath = target?.scopePath || [];
    const list = [];

    if (focusDim) {
      // dimension level — a dimension is focused with no committed value.
      const dl = store.dimension[focusDim] || store.dimensionDefault;
      if (dl) list.push(dl);
    } else {
      // value level — a matching `when` predicate beats a dim-wide value lens;
      // failing a value lens, a dim with its own dimension lens uses it.
      const last = scopePath.length ? scopePath[scopePath.length - 1] : null;
      if (last) {
        const forDim = store.value.filter((v) => v.dim === last.dim);
        const valueLens = forDim.find((v) => typeof v.when === "function" && v.when(last))
          || forDim.find((v) => typeof v.when !== "function");
        if (valueLens) list.push(valueLens);
        else if (store.dimension[last.dim] && !store.dimension[last.dim].dimensionOnly) {
          list.push(store.dimension[last.dim]);
        }
      }
    }

    // Every scope lens is normally available as a trailing toggle — the
    // overview first (it's registered first), then universal companions like
    // the endpoint Inventory. A primary lens can opt OUT of these companions
    // with `solo: true` (e.g. signals, whose client-side windowed scans don't
    // fit the server-joinable scope those lenses assume).
    if (!list[0]?.solo) {
      for (const sl of store.scopeLenses) {
        if (!list.includes(sl)) list.push(sl);
      }
    }
    return list;
  }

  /**
   * The default (most-specific) lens for a target — the head of resolveLenses.
   * @returns {Lens|null} null only when no scope lens has been registered.
   */
  function resolve(target) {
    return resolveLenses(target)[0] || null;
  }

  /**
   * The dimension lens that a FOCUSED dim resolves to — its own per-dim lens if
   * one is registered, else the generic member-rollup default. Mirrors the
   * focus branch of resolveLenses. The sidebar uses this to decide whether a dim
   * folder is lens-bearing (focuses an aggregate board on open + shows an
   * "All X" entry) without having to know which lens covers it.
   * @param {string} dimKey plural dim key
   * @returns {Lens|null}
   */
  function dimensionLensFor(dimKey) {
    return store.dimension[dimKey] || store.dimensionDefault || null;
  }

  const api = { register, resolve, resolveLenses, dimensionLensFor, store };
  return api;
}
